import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Checkbox from '@mui/material/Checkbox';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ParsedAddOn } from '@t/addOn.types';
import { formatPrice } from '@utils/formatPrice';
import { ChannelLogo } from '@shared/components/display/ChannelLogo/ChannelLogo';
import { ChannelHoverCard } from '@components/ProductOffers/misc/ChannelHoverCard';
import { channelTooltipProps } from '@components/ProductOffers/misc/TVChannelLogos.styles';
import { DiscountBadge } from '@shared/components/display/DiscountBadge/DiscountBadge';
import { SelectableCard } from '@shared/components/SelectableCard';
import { useChannelInfoTooltip } from '@hooks/useChannelInfoTooltip';

type IndividualChannelCardProps = {
  addOn: ParsedAddOn;
  isSelected: boolean;
  onToggle: () => void;
};

export const IndividualChannelCard = ({ addOn, isSelected, onToggle }: IndividualChannelCardProps) => {
  const { name, price, imageUrl, descriptionImage, description, discountTag, discountMessage } = addOn;
  const channel = { name, logo: imageUrl ?? '', description, descriptionImage: descriptionImage ?? imageUrl };

  // On a coarse pointer the logo tap opens the info-card and must NOT also toggle
  // selection, so stopPropagation keeps the tap from reaching SelectableCard's onClick.
  const { tooltipProps, onTriggerTap } = useChannelInfoTooltip({ stopPropagation: true });

  return (
    // SelectableCard stays outside the Tooltip: MUI Tooltip clones its child to attach
    // ref + hover/focus handlers, so the Card must remain the Tooltip's direct child.
    <SelectableCard selectionMode="multi" selected={isSelected} onSelect={onToggle}>
      {(selectable) => (
        <Tooltip
          title={<ChannelHoverCard channel={channel} />}
          placement="top"
          componentsProps={channelTooltipProps}
          {...tooltipProps}
        >
          <Card variant="individualChannelCard" selected={isSelected} elevation={0} {...selectable}>
            <Checkbox checked={isSelected} tabIndex={-1} disableRipple />

            <Stack className="IndividualChannelCard-content" gap="10px" alignItems="center">
              <Box className="IndividualChannelCard-logo" onClick={onTriggerTap}>
                <ChannelLogo src={imageUrl ?? ''} name={name} width="100%" height="100%" />
              </Box>

              <Typography
                variant="label.button"
                className="IndividualChannelCard-name"
                maxLines={1}
              >
                {name}
              </Typography>

              <Typography variant="body.md" className="IndividualChannelCard-price">
                {`${formatPrice(price)} / Month`}
              </Typography>

              {discountTag && (
                <DiscountBadge>
                  <Typography variant="discount.text" className="IndividualChannelCard-discountTag">
                    {discountTag}
                  </Typography>
                </DiscountBadge>
              )}

              {discountMessage && (
                <Typography variant="error.inline" className="IndividualChannelCard-discountMessage">
                  {discountMessage}. Discount applied at checkout
                </Typography>
              )}
            </Stack>
          </Card>
        </Tooltip>
      )}
    </SelectableCard>
  );
};
